<script setup>
import { computed, ref } from 'vue';
import { appStorage } from '@shared/utils/storage.js';

const props = defineProps({
	declinedRegistrationOnce: {
		type: Boolean,
		required: true,
	},
	isRegistrationPromptOpen: {
		type: Boolean,
		required: true,
	},
	toggleModals: {
		type: Function,
		required: true,
	},
});

const declineButtonLabel = computed((declinedRegistrationOnce) => {
	return props.declinedRegistrationOnce ? "Don't ask again" : "No thanks";
});

function declineToRegister() {
	props.isRegistrationPromptOpen = !props.isRegistrationPromptOpen;

	if (props.declinedRegistrationOnce) {
		appStorage.set("registrationPromptDeclined", true);
	} else {
		appStorage.set("registrationPromptDeclinedOnce", true);
	}
}

function createAccount() {
	props.toggleModals("auth-login");
}
</script>

<template>
<dialog id="register-prompt"
	:data-open="isRegistrationPromptOpen"
	:open="isRegistrationPromptOpen"
>
	<h3 class="my-0">Do you want to save your progress?</h3>
	<p>
		We don&#8217;t spy on our users or sell data to <strong>anyone</strong>.
		<span class="emoji">✌️</span>
	</p>
	<div class="inline-buttons">
		<a @click="declineToRegister()" class="btn secondary">{{
			declineButtonLabel
		}}</a>
		<a @click="createAccount()" class="btn primary">Create account</a>
	</div>
	<span id="backdrop"></span>
</dialog>
</template>

<style lang="scss" scoped>
#register-prompt {
	background-color: transparent;
	border: 1px solid var(--border);
	border-radius: 20px; /* match global-nav */
	color: var(--text1);
	display: block;
	padding: 16px;
	opacity: 0;
	overflow: visible;
	transition-property: right, left, opacity, translate;
	transition-duration: 300ms;
	transition-timing-function: ease;

	user-select: none;
	-webkit-user-select: none;

	position: fixed;
	left: calc(100vw + 16px);
	right: calc(200vw - 16px);
	bottom: 120px;
	margin: 0 -6px 0;
	width: calc(var(--main-content-width) - 20px - (17px * 2) - 1px);
	z-index: 15;
}
#register-prompt[data-open] {
	opacity: 1;
	translate: 0 0px;
}
#app:has(#sidebar.collapsed) #register-prompt {
	left: 16px;
	right: 16px;
}
#register-prompt h3 {
	font-size: 1.5rem;
	font-weight: 300;
	margin: 0;
}
#register-prompt p {
	margin-top: 0.5rem;
}
.show-1024 {
	display: none;
}
@media (min-width: 768px) {
	#app:has(#sidebar.collapsed) #register-prompt,
	#register-prompt {
		margin: 0;
		right: 10px;
		bottom: 64px;
		left: auto;
	}
}
@media (min-width: 1024px) {
	#app:has(#sidebar.collapsed) #register-prompt,
	#register-prompt {
		width: fit-content;
		inset: auto 0 10px;
		margin: 0 auto;

		display: flex;
		flex-flow: column;
	}
	.hide-1024 {
		display: none;
	}
	.show-1024 {
		display: block;
	}
}

/* auroras */
@keyframes aurora {
	0% { background-position: 0% 50%; }
	50% { background-position: 100% 50%; }
	100% { background-position: 0% 50%; }
}

#backdrop {
	background-color: var(--bg1a6);
  border: 1px solid var(--border);
	border-radius: 20px;
	backdrop-filter: blur(3px);
	display: block;

	position: absolute;
	z-index: -1;
	inset: 0;
}
#register-prompt::before,
#register-prompt::after {
	--gradient-rotation: -37deg;
	content:'';
	position: absolute;
	z-index: -2;
	inset: -2px;
	border-radius: 22px;
	background-position: 50% 50%;
	transition: background 0.5s ease-in-out;

	animation: auroraLight 5s infinite alternate;
	background: linear-gradient(var(--gradient-rotation),
		rgba(255, 195, 171, 0.4),
		rgba(225, 181, 255, 0.4),
		rgba(173, 190, 255, 0.4),
		rgba(193, 250, 255, 0.4),
		rgba(208, 255, 190, 0.4)
	);
	background-size: calc(3 * var(--main-content-width));
	@media(prefers-color-scheme:dark) {
		animation: auroraDark 5s infinite alternate;
		background: linear-gradient(var(--gradient-rotation),
			rgba(26,  115, 204, 0.6),
			rgba(18,  4,   52,  0.6),
			rgba(114, 26,  157, 0.6),
			rgba(8,   69,  65,  0.6),
			rgba(26,  115, 204, 0.6)
		);
		background-size: calc(3 * var(--main-content-width));
	}
}
#backdrop::after {
	filter: blur(8px);
	backdrop-filter: blur(5px);
	inset: -2px;
	z-index: -3;
}

@keyframes auroraLight {
	0% {
		// --gradient-rotation: 0deg;
		background-position: -100% 100%;
		filter: blur(2px) brightness(1.0);
	}
	100% {
		// --gradient-rotation: 100deg;
		background-position: 100% -100%;
		filter: blur(4px) brightness(1.2);
	}
}

@keyframes auroraDark {
	0% {
		// --gradient-rotation: 0deg;
		background-position: -100% 100%;
		filter: blur(2px) brightness(1.0);
	}
	100% {
		// --gradient-rotation: 100deg;
		background-position: 100% -100%;
		filter: blur(4px) brightness(.7);
	}
}
</style>
